import { Row } from '@tanstack/react-table';
import { Collaborator } from '@/models';
import { calculateConsolidatedStatus, normalizeStatus } from '@/utils/collaborator-status';

/**
 * Hook para fornecer funções de sorting personalizadas para status
 */
export const useStatusSorting = () => {
    const getStatusOrder = (status: string | null | undefined): number => {
        const normalized = normalizeStatus(status);

        switch (normalized) {
            case 'IN_PROGRESS':
                return 2; // meio
            case 'UPDATED':
                return 3; // último
            case 'PENDING':
            default:
                return 1; // primeiro
        }
    };

    /**
 * Função de sorting para status consolidado
 */
    const sortConsolidatedStatus = (rowA: Row<Collaborator>, rowB: Row<Collaborator>): number => {
        const statusA = calculateConsolidatedStatus(rowA.original);
        const statusB = calculateConsolidatedStatus(rowB.original);

        const orderA = getStatusOrder(statusA);
        const orderB = getStatusOrder(statusB);

        return orderA - orderB;
    };

    /**
 * Função de sorting para status DHO
 */
    const sortDhoStatus = (rowA: Row<Collaborator>, rowB: Row<Collaborator>): number => {
        const statusA = rowA.original.updatedStatus?.byDHO?.status;
        const statusB = rowB.original.updatedStatus?.byDHO?.status;

        return getStatusOrder(statusA) - getStatusOrder(statusB);
    };

    /**
 * Função de sorting para status DP
 */
    const sortDpStatus = (rowA: Row<Collaborator>, rowB: Row<Collaborator>): number => {
        const statusA = rowA.original.updatedStatus?.byDP?.status;
        const statusB = rowB.original.updatedStatus?.byDP?.status;

        // DEBUG: Logar comparação específica para ajudar a investigar o bug de ordenação
        if (import.meta.env.DEV) {
            const orderA = getStatusOrder(statusA);
            const orderB = getStatusOrder(statusB);
            console.debug(
                `🔍 DP sort — ${rowA.original.name} (${statusA ?? 'undefined'} → ${orderA}) vs ` +
                `${rowB.original.name} (${statusB ?? 'undefined'} → ${orderB}) = ${orderA - orderB}`
            );
        }

        return getStatusOrder(statusA) - getStatusOrder(statusB);
    };

    /**
 * Função de sorting para status Tech
 */
    const sortTechStatus = (rowA: Row<Collaborator>, rowB: Row<Collaborator>): number => {
        const statusA = rowA.original.updatedStatus?.byTech?.status;
        const statusB = rowB.original.updatedStatus?.byTech?.status;

        return getStatusOrder(statusA) - getStatusOrder(statusB);
    };

    // Sorting for User status
    const sortUserStatus = (rowA: Row<Collaborator>, rowB: Row<Collaborator>): number => {
        const statusA = rowA.original.updatedStatus?.byUser?.status;
        const statusB = rowB.original.updatedStatus?.byUser?.status;

        return getStatusOrder(statusA) - getStatusOrder(statusB);
    };

    /**
     * Função genérica para sorting de status baseada em accessor
     */
    const createStatusSortingFn = (accessor: (collaborator: Collaborator) => string | null | undefined) => {
        return (rowA: Row<Collaborator>, rowB: Row<Collaborator>): number => {
            const statusA = accessor(rowA.original);
            const statusB = accessor(rowB.original);

            return getStatusOrder(statusA) - getStatusOrder(statusB);
        };
    };

    return {
        sortConsolidatedStatus,
        sortDhoStatus,
        sortDpStatus,
        sortUserStatus,
        sortTechStatus,
        createStatusSortingFn,
    };
}; 